import React, { useState, useEffect } from 'react';
import styled from '@emotion/styled';
import { mediaQuery, Fonts, Spacings } from '@styles/index';
import CloseIcon from '@icon/Close';
import { isBannerClosedToday, closeBannerForToday } from './primeBannerStorage';

const Wrapper = styled.div`
  position: fixed;
  bottom: 0;
  left: 0;
  right: 0;
  display: flex;
  justify-content: center;
  z-index: 1300;
  padding: 0 ${Spacings.SPACING_4B} ${Spacings.SPACING_4B};

  ${mediaQuery.uptoMobile} {
    padding: 0 14px 14px;
  }
`;

const Container = styled.div`
  width: 940px;
  max-width: 100%;
  min-width: 0;
  height: 74px;
  min-height: 74px;
  flex-shrink: 0;
  display: flex;
  align-items: center;
  justify-content: space-between;
  border-radius: ${Spacings.SPACING_3B};
  border-bottom: 1px solid var(--color-i-violet-15);
  padding: ${Spacings.SPACING_3B};
  gap: ${Spacings.SPACING_2};
  background: var(--color-i-violet-15);

  ${mediaQuery.uptoMobile} {
    width: 100%;
    height: 64px;
    min-height: 64px;
    border-radius: ${Spacings.SPACING_2B};
  }
`;

const TextBlock = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${Spacings.SPACING_2};
  flex: 1;
  min-width: 0;

  ${mediaQuery.uptoMobile} {
    gap: 0;
  }
`;

const DesktopHeading = styled.span`
  font-size: ${Fonts.H5};
  font-weight: ${Fonts.SEMIBOLD};
  line-height: 24px;
  color: var(--color-i-white);

  ${mediaQuery.uptoMobile} {
    display: none;
  }
`;

const MwebHeading = styled.span`
  display: none;
  font-size: ${Fonts.H6};
  font-weight: ${Fonts.HEAVY};
  line-height: 20px;
  color: var(--color-i-white);

  ${mediaQuery.uptoMobile} {
    display: block;
  }
`;

const Description = styled.span`
  font-size: ${Fonts.H5};
  font-weight: ${Fonts.LIGHT};
  line-height: 24px;
  color: var(--color-i-white);

  ${mediaQuery.uptoMobile} {
    display: none;
  }
`;

const ActionsBlock = styled.div`
  display: flex;
  align-items: center;
  gap: ${Spacings.SPACING_3B};
  flex-shrink: 0;
`;

const ActionButton = styled.button`
  width: 162px;
  height: 44px;
  display: flex;
  align-items: center;
  justify-content: center;
  border-radius: ${Spacings.SPACING_2B};
  border: 1px solid var(--color-i-white);
  background: var(--color-i-white);
  padding: ${Spacings.SPACING_10} ${Spacings.SPACING_18};
  cursor: pointer;
  white-space: nowrap;

  font-size: ${Fonts.H5};
  font-weight: ${Fonts.SEMIBOLD};
  line-height: 24px;
  color: var(--color-i-violet-15);

  ${mediaQuery.uptoMobile} {
    width: 80px;
    height: 32px;
    border-radius: ${Spacings.SPACING_1B};
    padding: 0 ${Spacings.SPACING_10};
    font-size: ${Fonts.H6};
    font-weight: ${Fonts.HEAVY};
    line-height: 18px;
  }
`;

const DesktopButtonLabel = styled.span`
  ${mediaQuery.uptoMobile} {
    display: none;
  }
`;

const MwebButtonLabel = styled.span`
  display: none;

  ${mediaQuery.uptoMobile} {
    display: block;
  }
`;

const CloseButton = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  flex-shrink: 0;
  padding: 5px;
`;

const StyledCloseIcon = styled(CloseIcon)`
  fill: var(--color-i-white);
`;

interface PrimeBannerToastProps {
  heading: string;
  description?: string;
  mwebHeading?: string;
  buttonLabel: string;
  mwebButtonLabel?: string;
  onButtonClick: () => void;
  onClose?: () => void;
}

const defaultProps: PrimeBannerToastProps = {
  heading: 'Access Your Prime Experience',
  description: 'Access your Prime benefits seamlessly in the Unacademy app.',
  mwebHeading: 'Continue to the App for Your Prime Experience',
  buttonLabel: 'Continue on app',
  mwebButtonLabel: 'Continue',
  onButtonClick: () => {
    window.open('https://unacademy.onelink.me/cjVw/bp0hddpg', '_blank');
  },
  onClose: () => {}
};

const PrimeBannerToast: React.FC<PrimeBannerToastProps> = ({
  heading = defaultProps.heading,
  description = defaultProps.description,
  mwebHeading = defaultProps.mwebHeading,
  buttonLabel = defaultProps.buttonLabel,
  mwebButtonLabel = defaultProps.mwebButtonLabel,
  onButtonClick = defaultProps.onButtonClick,
  onClose = defaultProps.onClose
}) => {
  const [visible, setVisible] = useState(true);
  const [mounted, setMounted] = useState(false);

  useEffect(() => {
    setMounted(true);
  }, []);

  if (!mounted) return null;
  if (isBannerClosedToday()) return null;
  if (!visible) return null;

  const handleClose = () => {
    closeBannerForToday();
    setVisible(false);
    onClose?.();
  };

  return (
    <Wrapper>
      <Container>
        <TextBlock>
          <DesktopHeading>{heading}</DesktopHeading>
          {mwebHeading && <MwebHeading>{mwebHeading}</MwebHeading>}
          {description && <Description>{description}</Description>}
        </TextBlock>
        <ActionsBlock>
          <ActionButton onClick={onButtonClick}>
            <DesktopButtonLabel>{buttonLabel}</DesktopButtonLabel>
            <MwebButtonLabel>{mwebButtonLabel ?? buttonLabel}</MwebButtonLabel>
          </ActionButton>
          <CloseButton onClick={handleClose} aria-label="Close banner">
            <StyledCloseIcon
              height={16}
              width={16}
              color="var(--color-i-white)"
            />
          </CloseButton>
        </ActionsBlock>
      </Container>
    </Wrapper>
  );
};

export default PrimeBannerToast;
